import { useState, useEffect, useCallback, useMemo } from 'react';
import { ChatRoom, FilterOptions } from '../types';
import React from 'react';

export interface SearchState {
  searchTerm: string;
  activeFilters: FilterOptions;
  filteredResults: ChatRoom[];
  resultCount: number;
  loading: boolean;
  hasActiveFilters: boolean;
}

export interface SearchOperations {
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
  setFilter: <K extends keyof FilterOptions>(key: K, value: FilterOptions[K]) => void;
  clearFilter: (key: keyof FilterOptions) => void;
  clearAllFilters: () => void;
  toggleFilter: (key: keyof FilterOptions, value: any) => void;
  applyQuickFilter: (filterName: string) => void;
}

export interface UseSearchConfig {
  data: ChatRoom[];
  debounceMs?: number;
  searchFields?: (keyof ChatRoom)[];
  onResultsChange?: (results: ChatRoom[]) => void;
}

export const useSearch = (config: UseSearchConfig): SearchState & SearchOperations => {
  const { 
    data, 
    debounceMs = 300, 
    searchFields = ['title', 'last_message'],
    onResultsChange 
  } = config;

  const [searchTerm, setSearchTermState] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<FilterOptions>({});
  const [loading, setLoading] = useState(false);

  // 検索語句のデバウンス処理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setLoading(false);
    }, debounceMs);

    if (searchTerm !== debouncedSearchTerm) {
      setLoading(true);
    }

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm, debounceMs]);

  // フィルタリング・検索処理
  const filteredResults = useMemo(() => {
    let results = [...data];

    // テキスト検索
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      results = results.filter(item => {
        return searchFields.some(field => {
          const value = item[field];
          if (typeof value === 'string') {
            return value.toLowerCase().includes(searchLower);
          }
          if (typeof value === 'number') {
            return String(value).includes(searchLower);
          }
          return false;
        });
      });
    }

    // ステータスフィルター
    if (activeFilters.status && activeFilters.status.length > 0) {
      results = results.filter(item => 
        activeFilters.status!.includes(item.status)
      );
    }

    // 担当オペレーターフィルター
    if (activeFilters.assignedOperator) {
      results = results.filter(item => 
        item.created_by === activeFilters.assignedOperator ||
        item.created_by_name === activeFilters.assignedOperator
      );
    }

    // タグフィルター
    if (activeFilters.tags && activeFilters.tags.length > 0) {
      results = results.filter(item => {
        // TODO: 実際のタグシステム実装時に修正
        return activeFilters.tags!.some(tag => 
          item.title.toLowerCase().includes(tag.toLowerCase()) ||
          (item.last_message && item.last_message.toLowerCase().includes(tag.toLowerCase()))
        );
      });
    }

    // 日付範囲フィルター
    if (activeFilters.dateRange) {
      const { start, end } = activeFilters.dateRange;
      results = results.filter(item => {
        const itemDate = new Date(item.updated_at || item.created_at);
        return itemDate >= start && itemDate <= end;
      });
    }

    // 優先度フィルター（将来実装用）
    if (activeFilters.priority) {
      // TODO: priorityフィールドが実装されたら追加
    }

    return results;
  }, [data, debouncedSearchTerm, activeFilters, searchFields]);

  // アクティブフィルターの有無判定
  const hasActiveFilters = useMemo(() => {
    return Object.values(activeFilters).some(value => {
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') return true;
      return Boolean(value);
    });
  }, [activeFilters]);

  // 結果変更の通知
  useEffect(() => {
    onResultsChange?.(filteredResults);
  }, [filteredResults, onResultsChange]);

  // 検索語句設定
  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
  }, []);

  // 検索クリア
  const clearSearch = useCallback(() => {
    setSearchTermState('');
    setDebouncedSearchTerm('');
    setLoading(false);
  }, []);

  // フィルター設定
  const setFilter = useCallback(<K extends keyof FilterOptions>(key: K, value: FilterOptions[K]) => {
    setActiveFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // フィルタークリア
  const clearFilter = useCallback((key: keyof FilterOptions) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  }, []);

  // 全フィルタークリア
  const clearAllFilters = useCallback(() => {
    setActiveFilters({});
  }, []);

  // フィルター切り替え
  const toggleFilter = useCallback((key: keyof FilterOptions, value: any) => {
    setActiveFilters(prev => {
      const currentValue = prev[key];
      
      if (Array.isArray(currentValue)) {
        const newArray = currentValue.includes(value)
          ? currentValue.filter(item => item !== value)
          : [...currentValue, value];
        return { ...prev, [key]: newArray.length > 0 ? newArray : undefined };
      } else {
        return { ...prev, [key]: currentValue === value ? undefined : value };
      }
    });
  }, []);

  // クイックフィルター適用
  const applyQuickFilter = useCallback((filterName: string) => {
    switch (filterName) {
      case 'unread':
        setFilter('status', ['active']);
        break;
      case 'active':
        setFilter('status', ['active']);
        break;
      case 'completed':
        setFilter('status', ['completed']);
        break;
      case 'today':
        const today = new Date();
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        setFilter('dateRange', { start: startOfDay, end: endOfDay });
        break;
      case 'thisWeek':
        const now = new Date();
        const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
        const endOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 6, 23, 59, 59);
        setFilter('dateRange', { start: startOfWeek, end: endOfWeek });
        break;
      case 'vip':
        setFilter('tags', ['VIP']);
        break;
      case 'support':
        setFilter('tags', ['サポート', 'support']);
        break;
      case 'technical':
        setFilter('tags', ['技術', 'technical']);
        break;
      default:
        console.warn('未知のクイックフィルター:', filterName);
    }
  }, [setFilter]);

  return {
    // 状態
    searchTerm,
    activeFilters,
    filteredResults,
    resultCount: filteredResults.length,
    loading,
    hasActiveFilters,
    
    // 操作
    setSearchTerm,
    clearSearch,
    setFilter,
    clearFilter,
    clearAllFilters,
    toggleFilter,
    applyQuickFilter,
  };
};

// 検索ハイライト用ユーティリティ
export const highlightSearchTerm = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm.trim()) return text;

  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return React.createElement(React.Fragment, {}, 
    ...parts.map((part, index) => 
      regex.test(part) 
        ? React.createElement('mark', { 
            key: index, 
            style: { backgroundColor: '#ffeb3b', padding: '0 2px' } 
          }, part)
        : part
    )
  );
};

// 検索結果統計用ユーティリティ
export const getSearchStats = (results: ChatRoom[], originalData: ChatRoom[]) => {
  return {
    total: originalData.length,
    filtered: results.length,
    percentage: originalData.length > 0 ? Math.round((results.length / originalData.length) * 100) : 0,
    statusBreakdown: {
      active: results.filter(r => r.status === 'active').length,
      completed: results.filter(r => r.status === 'completed').length,
      archived: results.filter(r => r.status === 'archived').length,
    }
  };
};

export default useSearch;
